import type {
  RfidEpcScheme,
  RfidEpcSegment,
  RfidItemIdentifier,
  RfidSegmentSource,
} from '../types/rfid'

export interface ComposeEpcInput {
  scheme: RfidEpcScheme
  item?: Pick<RfidItemIdentifier, 'item_code' | 'style_code' | 'epc_year' | 'epc_season'> | null
  colorCode?: string | null
  sizeCode?: string | null
}

export interface ComposeEpcResult {
  epcBase: string | null
  missingSegments: string[]
  lengthIssues: string[]
  segmentValues: Record<string, string>
}

/**
 * 96-bit EPC (PC word 3000 = 6 words = 24 hex characters).
 * Base 16 + serial 8 = 24. The style code implies the item, so no item segment
 * is carried; `item_code` and `scheme_constant` remain valid segment sources for
 * schemes that want them.
 */
export const DEFAULT_RFID_SEGMENTS: RfidEpcSegment[] = [
  { key: 'version', label: 'Version', length: 1, source: 'scheme_version' },
  { key: 'year', label: 'Year', length: 2, source: 'item_year' },
  { key: 'season', label: 'Season', length: 1, source: 'item_season' },
  { key: 'style', label: 'Style', length: 6, source: 'item_style' },
  { key: 'color', label: 'Color', length: 3, source: 'color_code' },
  { key: 'size', label: 'Size', length: 3, source: 'size_code' },
]

export const DEFAULT_RFID_VERSION_CODE = '1'
export const DEFAULT_RFID_CONSTANT_CODE = ''
export const DEFAULT_RFID_SERIAL_LENGTH = 8
export const DEFAULT_RFID_TOTAL_LENGTH = 24
export const RFID_EPC_BITS_PER_WORD = 16
export const RFID_HEX_CHARS_PER_WORD = 4

export function getDefaultRfidSegments(): RfidEpcSegment[] {
  return DEFAULT_RFID_SEGMENTS.map((segment) => ({ ...segment }))
}

/** Word count the encoder must write, derived from the scheme rather than a literal. */
export function getSchemeWordCount(scheme: Pick<RfidEpcScheme, 'total_length'>): number {
  return Math.ceil(scheme.total_length / RFID_HEX_CHARS_PER_WORD)
}

/** EPC length in words lives in the top 5 bits of the PC word. */
export function wordCountFromPcWord(pcHex: string): number | null {
  const value = Number.parseInt(pcHex.trim(), 16)
  if (!Number.isFinite(value)) return null
  const words = (value >> 11) & 0x1f
  return words > 0 ? words : null
}

/** Inverse of wordCountFromPcWord: 6 words -> '3000'. */
export function pcWordForWordCount(words: number): string {
  return ((words & 0x1f) << 11).toString(16).toUpperCase().padStart(4, '0')
}

export function getSegmentBySource(
  scheme: RfidEpcScheme,
  source: RfidSegmentSource
): RfidEpcSegment | null {
  return scheme.segments.find((segment) => segment.source === source) ?? null
}

/**
 * An EPC is hexadecimal, so identifier codes that reach it must be decimal
 * digits of exactly the scheme's segment length. 'P271' is a display name, not
 * an encodable style code.
 */
export function validateIdentifierCode(params: {
  value: string
  length: number | null
  label: string
}): string | null {
  const value = params.value.trim()
  if (!value) return `${params.label} is required`
  if (!/^\d+$/.test(value))
    return `${params.label} must contain digits only (an EPC is hexadecimal, so "${value}" cannot be encoded)`
  if (params.length !== null && value.length !== params.length)
    return `${params.label} must be exactly ${params.length} digits (got ${value.length})`
  return null
}

/** Group an EPC by scheme segments so a wrong length is visible at a glance. */
export function formatEpcBySegments(scheme: RfidEpcScheme, epc: string): string {
  const value = epc.trim().toUpperCase()
  const parts: string[] = []
  let cursor = 0

  for (const segment of scheme.segments) {
    if (cursor >= value.length) break
    parts.push(value.slice(cursor, cursor + segment.length))
    cursor += segment.length
  }

  if (cursor < value.length) parts.push(value.slice(cursor))
  return parts.join(' ')
}

export function sumSegmentLengths(segments: RfidEpcSegment[]): number {
  return segments.reduce((sum, segment) => sum + (Number(segment.length) || 0), 0)
}

export function validateScheme(params: {
  segments: RfidEpcSegment[]
  serialLength: number
  totalLength: number
  versionCode?: string
  constantCode?: string
}): { isValid: boolean; errors: string[]; baseLength: number } {
  const errors: string[] = []
  const baseLength = sumSegmentLengths(params.segments)

  if (params.segments.length === 0)
    errors.push('At least one segment is required')

  for (const segment of params.segments) {
    if (!segment.key?.trim())
      errors.push('Every segment needs a key')
    if (!segment.label?.trim())
      errors.push(`Segment ${segment.key || '(unnamed)'} needs a label`)
    if (!Number.isInteger(segment.length) || segment.length < 1)
      errors.push(`Segment ${segment.key || segment.label} length must be a positive integer`)
  }

  if (!Number.isInteger(params.serialLength) || params.serialLength < 1)
    errors.push('Serial length must be a positive integer')

  if (!Number.isInteger(params.totalLength) || params.totalLength < 1)
    errors.push('Total length must be a positive integer')

  if (baseLength + params.serialLength !== params.totalLength) {
    errors.push(
      `Segments (${baseLength}) + serial (${params.serialLength}) must equal total (${params.totalLength})`
    )
  }

  if (params.versionCode !== undefined) {
    const versionSegment = params.segments.find((s) => s.source === 'scheme_version')
    if (versionSegment && params.versionCode.length !== versionSegment.length) {
      errors.push(
        `Version code must be ${versionSegment.length} character(s) (got ${params.versionCode.length})`
      )
    }
  }

  if (params.constantCode !== undefined) {
    const constantSegment = params.segments.find((s) => s.source === 'scheme_constant')
    if (constantSegment && params.constantCode.length !== constantSegment.length) {
      errors.push(
        `Constant code must be ${constantSegment.length} character(s) (got ${params.constantCode.length})`
      )
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    baseLength,
  }
}

function resolveSegmentValue(
  source: RfidSegmentSource,
  input: ComposeEpcInput
): string | null {
  switch (source) {
    case 'scheme_version':
      return input.scheme.version_code ?? null
    case 'item_year':
      return input.item?.epc_year ?? null
    case 'item_season':
      return input.item?.epc_season ?? null
    case 'item_code':
      return input.item?.item_code ?? null
    case 'item_style':
      return input.item?.style_code ?? null
    case 'color_code':
      return input.colorCode ?? null
    case 'size_code':
      return input.sizeCode ?? null
    case 'scheme_constant':
      return input.scheme.constant_code ?? null
    default:
      return null
  }
}

const DIGIT_ONLY_SOURCES: RfidSegmentSource[] = [
  'item_year',
  'item_season',
  'item_code',
  'item_style',
  'color_code',
  'size_code',
]

export function composeEpcBase(input: ComposeEpcInput): ComposeEpcResult {
  const missingSegments: string[] = []
  const lengthIssues: string[] = []
  const segmentValues: Record<string, string> = {}
  const parts: string[] = []

  for (const segment of input.scheme.segments) {
    const raw = resolveSegmentValue(segment.source, input)
    const value = raw?.trim() ?? ''

    if (!value) {
      missingSegments.push(segment.label)
      segmentValues[segment.key] = ''
      continue
    }

    if (value.length !== segment.length) {
      lengthIssues.push(
        `${segment.label} must be ${segment.length} character(s) (got ${value.length})`
      )
    }

    if (DIGIT_ONLY_SOURCES.includes(segment.source) && !/^\d+$/.test(value)) {
      lengthIssues.push(
        `${segment.label} "${value}" is not encodable — identifier codes must be digits only`
      )
    } else if (!/^[0-9A-Fa-f]+$/.test(value)) {
      lengthIssues.push(`${segment.label} "${value}" is not hexadecimal`)
    }

    segmentValues[segment.key] = value
    parts.push(value)
  }

  const epcBase =
    missingSegments.length === 0 && lengthIssues.length === 0
      ? parts.join('')
      : null

  return {
    epcBase,
    missingSegments,
    lengthIssues,
    segmentValues,
  }
}

export function formatEpcPreview(params: {
  epcBase: string | null
  serialLength: number
  serialPlaceholder?: string
}): { fullPreview: string; serialPlaceholder: string } {
  const serialPlaceholder =
    params.serialPlaceholder ??
    '0'.repeat(Math.max(0, params.serialLength))

  if (!params.epcBase) {
    return {
      fullPreview: '',
      serialPlaceholder,
    }
  }

  return {
    fullPreview: `${params.epcBase}${serialPlaceholder}`,
    serialPlaceholder,
  }
}

export interface DecodeEpcFields {
  versionCode: string | null
  epcYear: string | null
  epcSeason: string | null
  itemCode: string | null
  styleCode: string | null
  colorCode: string | null
  sizeCode: string | null
  constantCode: string | null
}

export interface DecodeEpcResult {
  isValid: boolean
  errors: string[]
  epcFull: string
  epcBase: string
  serial: string
  segmentValues: Record<string, string>
  fields: DecodeEpcFields
}

function emptyDecodeFields(): DecodeEpcFields {
  return {
    versionCode: null,
    epcYear: null,
    epcSeason: null,
    itemCode: null,
    styleCode: null,
    colorCode: null,
    sizeCode: null,
    constantCode: null,
  }
}

function assignDecodedField(
  fields: DecodeEpcFields,
  source: RfidSegmentSource,
  value: string
): void {
  switch (source) {
    case 'scheme_version':
      fields.versionCode = value
      break
    case 'item_year':
      fields.epcYear = value
      break
    case 'item_season':
      fields.epcSeason = value
      break
    case 'item_code':
      fields.itemCode = value
      break
    case 'item_style':
      fields.styleCode = value
      break
    case 'color_code':
      fields.colorCode = value
      break
    case 'size_code':
      fields.sizeCode = value
      break
    case 'scheme_constant':
      fields.constantCode = value
      break
  }
}

/**
 * Exact inverse of composeEpcBase. Layout comes only from scheme.segments
 * and scheme.serial_length — never hardcoded offsets.
 */
export function decodeEpc(scheme: RfidEpcScheme, epcRaw: string): DecodeEpcResult {
  const epcFull = epcRaw.trim().toUpperCase()
  const errors: string[] = []
  const segmentValues: Record<string, string> = {}
  const fields = emptyDecodeFields()

  if (!/^[0-9A-F]+$/.test(epcFull))
    errors.push('EPC must be hexadecimal')

  if (epcFull.length !== scheme.total_length) {
    errors.push(
      `EPC length ${epcFull.length} does not match scheme total ${scheme.total_length}`
    )
  }

  const serialLength = scheme.serial_length
  if (!Number.isInteger(serialLength) || serialLength < 1)
    errors.push('Scheme serial_length is invalid')

  const baseLength = sumSegmentLengths(scheme.segments)
  if (baseLength + serialLength !== scheme.total_length) {
    errors.push(
      `Scheme segments (${baseLength}) + serial (${serialLength}) must equal total (${scheme.total_length})`
    )
  }

  if (errors.length > 0) {
    return {
      isValid: false,
      errors,
      epcFull,
      epcBase: '',
      serial: '',
      segmentValues,
      fields,
    }
  }

  const epcBase = epcFull.slice(0, -serialLength)
  const serial = epcFull.slice(-serialLength)

  let cursor = 0
  for (const segment of scheme.segments) {
    const value = epcBase.slice(cursor, cursor + segment.length)
    if (value.length !== segment.length) {
      errors.push(`Could not read segment ${segment.label}`)
      break
    }
    segmentValues[segment.key] = value
    assignDecodedField(fields, segment.source, value)
    cursor += segment.length
  }

  if (cursor !== epcBase.length && errors.length === 0)
    errors.push('Decoded base length does not match scheme segments')

  return {
    isValid: errors.length === 0,
    errors,
    epcFull,
    epcBase,
    serial,
    segmentValues,
    fields,
  }
}
